import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { useStore } from "../../../../hooks/useStore";
import { useTranslation } from "../../../../hooks/useTranslation";
import { authorizationTagLabels, authorizationTagVariant } from "../Table/utils/getInitials";
import { BadgeTypes } from "../Badge/Badge";
import MultiSelect, { MultiSelectOption } from "../Select/MultiSelect";
import Select, { SelectOption } from "../Select/Select";
import useTransportCompanies from "../../../../views/LogisticPark/RegistroIngreso/components/AccessTypeForm/TransportContentForm/useTransportCompanies";
import TextInput from "../TextInput/TextInput";
import SearchIcon from "@material-ui/icons/Search";
import useVehicleTypes from "../../../../hooks/useVehicleTypes";

/** Authorization tags accepted by the endpoint, in display order. */
export const AUTHORIZATION_TAG_ORDER = [
  "employee",
  "visit",
  "eventual",
  "contractor",
  "preauthorized",
  "guard",
  "transport",
] as const;

interface PeopleFiltersProps {
  /** Búsqueda contra el `search` del endpoint. Sin `onSearchChange` el campo no se renderiza, para
   * las pantallas que ya tienen su propio buscador. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  selectedCompanies: string[];
  onCompaniesChange: (value: string[]) => void;
  selectedAuthorizationTags: string[];
  onAuthorizationTagsChange: (value: string[]) => void;
  selectedTransportCompanies: string[];
  onTransportCompaniesChange: (value: string[]) => void;
  /** En la vista por vehículo el selector de tipo de autorización se cambia por el de carrocería.
   * Ese filtro no lo soporta `getPeopleInside`, así que lo aplica la tabla sobre lo ya cargado. */
  vehicleTypeMode?: boolean;
  selectedVehicleType?: string;
  onVehicleTypeChange?: (value: string) => void;
  /** Controles propios de la pantalla (p. ej. el toggle de vista), alineados a la derecha. */
  trailing?: React.ReactNode;
  className?: string;
}

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    padding: "16px 20px",
    backgroundColor: "#ffffff",
    border: "1px solid #E2E8F0",
    borderRadius: "10px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.06)",
  },
  field: {
    flex: "1 1 260px",
    minWidth: "220px",
  },
  trailing: {
    flex: "0 0 auto",
    marginLeft: "auto",
    display: "flex",
    // Los campos llevan label arriba, así que alineando abajo el control queda a la altura de
    // los inputs y no flotando contra las etiquetas.
    alignItems: "flex-end",
  },
}));

const PeopleFilters = ({
  searchValue = "",
  onSearchChange,
  selectedCompanies,
  onCompaniesChange,
  selectedAuthorizationTags,
  onAuthorizationTagsChange,
  selectedTransportCompanies,
  onTransportCompaniesChange,
  vehicleTypeMode,
  selectedVehicleType = "",
  onVehicleTypeChange,
  trailing,
  className,
}: PeopleFiltersProps) => {
  const classes = useStyles();
  const { t, BundleKey } = useTranslation();
  const { authStore } = useStore();
  // Las transportistas no viven en el authStore como el resto de las empresas: se traen del
  // mismo endpoint que usa el alta de transporte.
  const { companies: transportCompanies } = useTransportCompanies();
  const { vehicleTypes } = useVehicleTypes();

  const companyOptions = useMemo<MultiSelectOption[]>(() => {
    return (authStore.companies || [])
      .filter((company: any) => company?.id !== undefined && company?.id !== null)
      .map((company: any) => ({
        value: String(company.id),
        label: company.name || company.description || String(company.id),
      }))
      .sort((a: MultiSelectOption, b: MultiSelectOption) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );
  }, [authStore.companies]);

  const transportCompanyOptions = useMemo<MultiSelectOption[]>(() => {
    return (transportCompanies || [])
      .filter((company: any) => company?.id !== undefined && company?.id !== null)
      .map((company: any) => ({
        value: String(company.id),
        label: company.name || company.description || String(company.id),
      }))
      .sort((a: MultiSelectOption, b: MultiSelectOption) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );
  }, [transportCompanies]);

  /**
   * El "todos" va como opción vacía: el Select simple no tiene placeholder seleccionable.
   *
   * El value es el **nombre** y no el id del catálogo porque `getVehiclesInside` devuelve
   * `vehicleTypeName` (un snapshot del evento) y no acepta filtrar por tipo: el recorte lo hace la
   * tabla comparando contra ese nombre.
   */
  const vehicleTypeOptions = useMemo(
    () => [
      { value: "", label: "Todos los tipos" },
      ...vehicleTypes
        .map((type) => ({ value: type.label, label: type.label }))
        .sort((a: SelectOption, b: SelectOption) => b.label.localeCompare(a.label)),
    ],
    [vehicleTypes]
  );

  const authorizationOptions = useMemo<MultiSelectOption[]>(() => {
    return AUTHORIZATION_TAG_ORDER.map((tag) => ({
      value: tag,
      label: authorizationTagLabels[tag] ?? tag,
      variant: (authorizationTagVariant[tag] as BadgeTypes) ?? "blue",
    }));
  }, []);

  return (
    <div className={[classes.container, className].filter(Boolean).join(" ")}>
      {onSearchChange && (
        <div className={classes.field}>
          <TextInput
            label={t(BundleKey.PEOPLEFILTERS_SEARCH_LABEL)}
            placeholder={t(BundleKey.PEOPLEFILTERS_SEARCH_PLACEHOLDER)}
            value={searchValue}
            onChange={onSearchChange}
            leftIcon={<SearchIcon fontSize="small" style={{ color: "#94A3B8" }} />}
          />
        </div>
      )}

      <div className={classes.field}>
        <MultiSelect
          label={t(BundleKey.PEOPLEFILTERS_COMPANY_LABEL)}
          placeholder={t(BundleKey.PEOPLEFILTERS_COMPANY_PLACEHOLDER)}
          options={companyOptions}
          value={selectedCompanies}
          onChange={onCompaniesChange}
          isSearchable
        />
      </div>

      <div className={classes.field}>
        <MultiSelect
          label={t(BundleKey.PEOPLEFILTERS_TRANSPORT_COMPANY_LABEL)}
          placeholder={t(BundleKey.PEOPLEFILTERS_TRANSPORT_COMPANY_PLACEHOLDER)}
          options={transportCompanyOptions}
          value={selectedTransportCompanies}
          onChange={onTransportCompaniesChange}
          isSearchable
        />
      </div>

      <div className={classes.field}>
        {vehicleTypeMode ? (
          <Select
            label="Tipo de vehículo"
            placeholder="Todos los tipos"
            options={vehicleTypeOptions}
            value={selectedVehicleType}
            onChange={(value) => onVehicleTypeChange && onVehicleTypeChange(String(value))}
          />
        ) : (
          <MultiSelect
            label={t(BundleKey.PEOPLEFILTERS_AUTHORIZATION_LABEL)}
            placeholder={t(BundleKey.PEOPLEFILTERS_AUTHORIZATION_PLACEHOLDER)}
            options={authorizationOptions}
            value={selectedAuthorizationTags}
            onChange={onAuthorizationTagsChange}
            maxChips={3}
          />
        )}
      </div>

      {trailing && <div className={classes.trailing}>{trailing}</div>}
    </div>
  );
};

export default PeopleFilters;
